import { Eye } from 'lucide-react';
import { twMerge } from 'tailwind-merge';
import { useTranslation } from 'react-i18next';
import ProfileAvatar from '../common/ProfileAvatar';
import RatingStars from '../common/RatingStars';
import { useState } from 'react';

import type { Contact } from '../../types/contact';
import { useProfileRole } from '../../hooks/useProfileRole';
import ContactModal from './ContactModal';
import { getFreelancerSubtitle } from '../../utils/freelancerUtils';

interface ContactCardProps {
  contact: Contact;
  onClick?: () => void;
  complete?: boolean;
  className?: string;
  alternate?: boolean;
}

export default function ContactCard({
  contact,
  onClick,
  complete = false,
  className,
}: ContactCardProps) {
  const { t } = useTranslation(['freelancers', 'common']);
  const { isRecruiter, isFreelancer } = useProfileRole();
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);

  const name = `${contact.first_name ?? ''} `.trim();
  const avatarUrl = contact.avatar_url;
  const subtitle = isRecruiter ? (contact.job_title ?? '') : (contact.company_name ?? '');
  const role = contact.role ?? '';

  const getAllianceCount = (count: number | undefined): string => {
    if (!count) return '0';
    if (count > 10) return '10+';
    return String(count);
  };

  const allianceCount = getAllianceCount(contact.alliance_count);

  const handleCardClick = () => {
    setSelectedContact(contact);
    onClick?.();
  };

  return (
    <div>
      <div
        className={twMerge(
          `grid grid-cols-[3rem_1fr] gap-4 py-3 hover:cursor-pointer md:grid-cols-[50px_5fr_1fr] md:items-center`,
          className
        )}
        onClick={handleCardClick}
        role="button"
        tabIndex={0}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') handleCardClick();
        }}
      >
        <ProfileAvatar
          profile_id={contact.id}
          first_name={contact.first_name}
          avatar_url={avatarUrl}
          className="flex-shrink-0"
        />

        <div>
          {/* VERSION MOBILE */}
          <div className="md:hidden w-full">
            <div className="grid grid-cols-[70%_30%] gap-y-2 w-full">
              <div className="pr-4 min-w-0 flex flex-col items-start">
                <div
                  className={`text-base leading-tight ${!isFreelancer ? 'font-bold' : ''} truncate text-left`}
                >
                  {name}
                </div>
                <div className="text-xs text-[#5A6B82] mt-1">
                  {role && (
                    <span className="text-xs text-[#5A6B82]">
                      {getFreelancerSubtitle(contact, t)}
                    </span>
                  )}
                  {isFreelancer && <span className="text-xs text-[#5A6B82]">{subtitle}</span>}
                </div>
              </div>
              {isFreelancer && (
                <button
                  type="button"
                  className="flex items-center justify-center h-8 w-8 rounded-full border border-[#A3B2C2] bg-white hover:bg-gray-100 flex-shrink-0 min-w-fit min-h-fit ml-2 md:flex md:justify-self-end"
                  aria-label={t('quotes:createQuoteModal.showAllianceButtonLabel')}
                  onClick={e => {
                    e.stopPropagation();
                    setSelectedContact(contact);
                  }}
                >
                  <Eye className="h-5 w-5 text-secondary" />
                </button>
              )}

              {!isFreelancer && (
                <div className="flex justify-end items-center">
                  <button
                    type="button"
                    className="h-8 w-8 rounded-full border border-[#A3B2C2] bg-white hover:bg-gray-100 flex items-center justify-center"
                    aria-label={t('quotes:createQuoteModal.showAllianceButtonLabel')}
                    onClick={e => {
                      e.stopPropagation();
                      setSelectedContact(contact);
                    }}
                  >
                    <Eye className="h-5 w-5 text-secondary" />
                  </button>
                </div>
              )}

              <div className="flex flex-col">
                {isRecruiter && complete && (
                  <div className="flex gap-4 items-center">
                    <div className="flex flex-col gap-1">
                      <span className="text-sm">{t('rating', { ns: 'freelancers' })}</span>
                      <RatingStars rating={contact.stats?.overall_rating ?? 0} />
                    </div>
                  </div>
                )}
              </div>

              {isRecruiter && complete && (
                <div className="flex flex-col gap-1 items-end">
                  <span className="text-sm">{t('alliances', { ns: 'freelancers' })}</span>
                  <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                    <span className="text-white text-xs">{allianceCount}</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* version desktop */}
          <div className="hidden md:block w-full">
            <div className="flex min-w-0 flex-1 items-start justify-between gap-3">
              <div className="flex min-w-0 flex-1 flex-col pr-3">
                <span className="mb-2 text-sm font-medium text-secondary">{name}</span>
                {complete && isRecruiter && contact.services && (
                  <span className="break-words text-sm text-secondary">
                    {getFreelancerSubtitle(contact, t)}
                  </span>
                )}
              </div>

              {isRecruiter && complete && (
                <div className="flex flex-shrink-0 items-start gap-3">
                  <div className="flex flex-col gap-1">
                    <span className="text-xs text-secondary">
                      {t('rating', { ns: 'freelancers' })}
                    </span>
                    <RatingStars rating={contact.stats?.overall_rating ?? 0} />
                  </div>

                  <div className="flex flex-col gap-1">
                    <span className="text-xs text-secondary">
                      {t('alliances', { ns: 'freelancers' })}
                    </span>
                    <span className="flex items-center justify-center rounded-full bg-primary h-6 w-6 text-xs font-medium text-white">
                      {allianceCount}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <button
          type="button"
          className="hidden items-center justify-center h-8 w-8 rounded-full border border-[#A3B2C2] bg-white hover:bg-gray-100 flex-shrink-0 min-w-fit min-h-fit ml-2 md:flex md:justify-self-end"
          aria-label={t('quotes:createQuoteModal.showAllianceButtonLabel')}
          onClick={e => {
            e.stopPropagation();
            setSelectedContact(contact);
          }}
        >
          <Eye className="h-5 w-5 text-secondary" />
        </button>
      </div>

      {selectedContact && (
        <ContactModal contact={selectedContact} onClose={() => setSelectedContact(null)} />
      )}
    </div>
  );
}
